// src/pages/ContactUs.js
import React, { useState, useEffect } from 'react';
import ContactInfoEditor from '../components/ContactInfoEditor';
import FacebookUrlEditor from '../components/FacebookUrlEditor';

function ContactUs({ isAdmin, onLogout }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    gdpr: false
  });
  
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [contactInfo, setContactInfo] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isFacebookUrlEditorOpen, setIsFacebookUrlEditorOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoadingContactInfo, setIsLoadingContactInfo] = useState(true);
  const [facebookUrl, setFacebookUrl] = useState('');
  
  // Fetch contact info from API on component mount
  useEffect(() => {
    fetchContactInfo();
  }, []);

  // Fetch contact info from the API
  const fetchContactInfo = async () => {
    try {
      setIsLoadingContactInfo(true);
      const response = await fetch('https://eurocentrumv1.onrender.com/api/site-config');
      
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data.contactInfo || '');
        setFacebookUrl(data.facebookUrl || 'https://www.facebook.com/');
      } else {
        console.error('Failed to fetch contact info');
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    } finally {
      setIsLoadingContactInfo(false);
    }
  };

  // Save updated contact info
  const saveContactInfo = async (newContent) => {
    const response = await fetch('https://eurocentrumv1.onrender.com/api/site-config/contact-info', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contactInfo: newContent }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to save contact info');
    }
    
    // Update local state with new content
    setContactInfo(newContent);
  };
  
  // Save updated Facebook URL
  const saveFacebookUrl = async (newUrl) => {
    const response = await fetch('https://eurocentrumv1.onrender.com/api/site-config/facebook-url', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ facebookUrl: newUrl }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to save Facebook URL');
    }
    
    // Update local state with new URL
    setFacebookUrl(newUrl);
  };
  
  // Handle responsive layout without CSS media queries
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Reset submit message
    setSubmitMessage({ type: '', text: '' });
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím vyplňte všetky povinné polia.'
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím zadajte platný email.'
      });
      return;
    }
    
    // GDPR validation
    if (!formData.gdpr) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím potvrďte súhlas so spracovaním osobných údajov.'
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Display interim message
      setSubmitMessage({
        type: 'info',
        text: 'Odosielanie správy...'
      });
      
      console.log('Sending email with data:', formData);
      
      // Try new simplified endpoint first
      try {
        const response = await fetch('https://eurocentrumv1.onrender.com/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: 'eurocentrum.reality@gmail.com', // Updated recipient email
            subject: formData.subject || `Nová správa od ${formData.name}`,
            text: `Meno: ${formData.name}\nEmail: ${formData.email}\n\nSpráva:\n${formData.message}`,
            html: `
              <h2>Nová správa z kontaktného formulára</h2>
              <p><strong>Meno:</strong> ${formData.name}</p>
              <p><strong>Email:</strong> ${formData.email}</p>
              <p><strong>Predmet:</strong> ${formData.subject || 'Nie je uvedený'}</p>
              <p><strong>Správa:</strong></p>
              <p>${formData.message.replace(/\n/g, '<br>')}</p>
            `
          }),
        });
        
        console.log('Response status:', response.status);
        const result = await response.json();
        console.log('Response data:', result);
        
        if (response.ok) {
          // Success
          setSubmitMessage({
            type: 'success',
            text: 'Správa bola úspešne odoslaná! Ďakujeme za Váš záujem.'
          });
          // Reset form
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: '',
            gdpr: false
          });
          return;
        }
      } catch (directError) {
        console.error('Error with direct endpoint, falling back to original:', directError);
      }
      
      // Fallback to original endpoint if direct one fails
      const response = await fetch('https://eurocentrumv1.onrender.com/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      console.log('Original endpoint status:', response.status);
      const result = await response.json();
      console.log('Original endpoint data:', result);
      
      if (response.ok) {
        // Success
        setSubmitMessage({
          type: 'success',
          text: 'Správa bola úspešne odoslaná! Ďakujeme za Váš záujem.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          gdpr: false
        });
      } else {
        // Error from server
        setSubmitMessage({
          type: 'error',
          text: result.message || 'Nastala chyba pri odosielaní správy.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Nastala chyba pri odosielaní správy. Skúste to znova neskôr.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Merge responsive styles
  const contentWrapperStyle = {
    ...styles.contentWrapper,
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? '2rem' : '3rem'
  };
  
  const containerStyle = {
    ...styles.container,
    padding: isMobile ? '2rem 1rem' : '3rem'
  };

  return (
    <div style={styles.pageWrapper}>
      {/* Main container for the page content with gradient & shadow */}
      <div style={containerStyle}>
        {/* Title */}
        <h1 style={styles.title}>Kontaktné údaje</h1>

        {/* Two-column layout (grid-based) */}
        <div style={contentWrapperStyle}>
          {/* Left column: the form */}
          <div style={styles.leftColumn}>
            <h2 style={styles.formHeading}>Napíšte nám správu</h2>

            {submitMessage.text && (
              <div 
                style={{
                  ...styles.messageBox,
                  backgroundColor: 
                    submitMessage.type === 'success' ? '#dff2d8' : 
                    submitMessage.type === 'info' ? '#d1ecf1' : 
                    '#f8d7da',
                  color: 
                    submitMessage.type === 'success' ? '#3c763d' : 
                    submitMessage.type === 'info' ? '#0c5460' : 
                    '#721c24',
                  padding: '15px',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontWeight: 'bold',
                }}
              >
                {submitMessage.text}
              </div>
            )}

            <form style={styles.form} onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="name">Vaše meno</label>
                <input 
                  type="text" 
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={styles.input} 
                  placeholder="Zadajte meno" 
                  required 
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="email">Váš email</label>
                <input 
                  type="email" 
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={styles.input} 
                  placeholder="Zadajte email" 
                  required 
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="subject">Predmet</label>
                <input 
                  type="text" 
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  style={styles.input} 
                  placeholder="Zadajte predmet" 
                  required 
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="message">Vaša správa</label>
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6} 
                  style={styles.textarea} 
                  placeholder="Tu napíšte správu..." 
                />
              </div>

              <div style={styles.checkboxContainer}>
                <input 
                  type="checkbox" 
                  id="gdpr" 
                  name="gdpr"
                  checked={formData.gdpr}
                  onChange={handleChange}
                  style={styles.checkbox} 
                  required 
                />
                <label htmlFor="gdpr" style={styles.checkboxLabel}>
                  Súhlasím so spracovaním osobných údajov
                </label>
              </div>

              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Odosielanie...' : 'Odoslať správu'}
              </button>
            </form>
          </div>

          {/* Right column: contact info with a colored background */}
          <div 
            style={styles.rightColumn}
            onMouseEnter={() => isAdmin && setIsHovered(true)}
            onMouseLeave={() => isAdmin && setIsHovered(false)}
          >
            <h2 style={styles.formHeading}>Kontaktné údaje</h2>
            
            {/* Edit icons for admin */}
            {isAdmin && isHovered && (
              <>
                <div 
                  style={styles.editIconContainer}
                  onClick={() => setIsEditorOpen(true)}
                  title="Edit Contact Information"
                >
                  <span style={styles.editIcon}>✏️</span>
                </div>
                
                <button
                  style={styles.facebookUrlButton}
                  onClick={() => setIsFacebookUrlEditorOpen(true)}
                >
                  Nastaviť link na Facebook
                </button>
              </>
            )}
            
            <div style={styles.contactCard}>
              {isLoadingContactInfo ? (
                <div style={styles.loadingContainer}>
                  <div style={styles.loadingSpinner}></div>
                  <p style={styles.loadingText}>Loading contact information...</p>
                </div>
              ) : (
                <div 
                  dangerouslySetInnerHTML={{ __html: contactInfo }}
                  style={styles.contactContent}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 100% width Google Maps integration below all content */}
      <div style={styles.mapWrapper}>
        <iframe
          style={styles.mapFrame}
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5289.1859871111195!2d18.60291951590261!3d48.76903358036168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4714e9265b476feb%3A0x9df2d44ba8a3c188!2sPrievidza!5e0!3m2!1scs!2ssk!4v1692299999999"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Google Map"
        />
      </div>
      
      {/* Contact Info Editor Modal */}
      <ContactInfoEditor 
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        initialContent={contactInfo}
        onSave={saveContactInfo}
      />
      
      {/* Facebook URL Editor Modal */}
      <FacebookUrlEditor
        isOpen={isFacebookUrlEditorOpen}
        onClose={() => setIsFacebookUrlEditorOpen(false)}
        initialUrl={facebookUrl}
        onSave={saveFacebookUrl}
      />
      
      {/* Global styles to fix overflow issues */}
      <style>{`
        body {
          overflow-x: hidden;
          margin: 0;
          padding: 0;
          width: 100%;
        }
        
        * {
          box-sizing: border-box;
        }
        
        /* Loading spinner animation */
        @keyframes spinner {
          to {transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
}

export default ContactUs;

const styles = {
  pageWrapper: {
    width: '100%',
    margin: 0,
    padding: 0,
    fontFamily: 'Inter, system-ui, sans-serif',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    position: 'relative',
    maxWidth: '100vw',
  },
  container: {
    maxWidth: '1200px',
    width: '95%', // Slightly less than 100% to prevent overflow
    margin: '2rem auto',
    marginTop: '90px',
    padding: '3rem',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    background: `
      linear-gradient(
        180deg,
        rgba(44, 105, 160, 0.85) 0%,
        rgba(28, 63, 115, 0.9) 100%
      ),
      url('https://www.eurocentrumprievidza.sk/wp-content/uploads/2024/02/architecture-4752275_1920-1.jpg')
        center center / cover no-repeat
    `,
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: '2rem',
    color: '#fff',
    textAlign: 'center',
    wordWrap: 'break-word',
  },
  contentWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '3rem',
    alignItems: 'stretch',
    width: '100%',
    boxSizing: 'border-box',
  },
  leftColumn: {
    backgroundColor: 'transparent',
  },
  formHeading: {
    fontSize: '1.8rem',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '1.5rem',
  },
  form: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    padding: '2rem',
    paddingRight: '3rem',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    width: '100%',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#333',
    fontSize: '0.95rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    outline: 'none',
    '&:focus': {
      borderColor: '#2c69a0',
      boxShadow: '0 0 0 3px rgba(44, 105, 160, 0.2)',
    },
    '&::placeholder': {
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#aaa',
      fontSize: '0.9rem',
    },
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    fontFamily: 'Inter, system-ui, sans-serif',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    resize: 'vertical',
    outline: 'none',
    minHeight: '120px',
    '&:focus': {
      borderColor: '#2c69a0',
      boxShadow: '0 0 0 3px rgba(44, 105, 160, 0.2)',
    },
    '&::placeholder': {
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#aaa',
      fontSize: '0.9rem',
    },
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
    gap: '0.5rem',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#2c69a0',
    cursor: 'pointer',
  },
  checkboxLabel: {
    color: '#555',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  submitButton: {
    backgroundColor: '#2c69a0',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s, transform 0.2s',
    display: 'block',
    marginLeft: 'auto',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: '#1c3f73',
      transform: 'translateY(-2px)',
    },
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    position: 'relative',
  },
  contactCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    padding: '2rem',
    borderRadius: '12px',
    color: 'white',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    maxHeight: 'calc(100% - 3.3rem)',
    overflow: 'auto',
  },
  contactTitle: {
    fontSize: '1.8rem',
    fontWeight: '700',
    marginBottom: '0.25rem',
    color: 'white',
    display: 'none', // Hide this since we're using formHeading instead
  },
  subtitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '1.5rem',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 0,
  },
  contactItem: {
    display: 'flex',
    marginBottom: '1.25rem',
  },
  icon: {
    fontSize: '1.25rem',
    marginRight: '1rem',
    marginTop: '0.25rem',
  },
  contactText: {
    margin: 0,
    lineHeight: 1.6,
    fontSize: '1rem',
  },
  socialLinks: {
    marginTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  socialItem: {
    marginBottom: '0.5rem',
  },
  socialText: {
    fontSize: '1rem',
    lineHeight: '1.5',
  },
  mapWrapper: {
    width: '100%',
    marginTop: '3rem',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxSizing: 'border-box',
    maxWidth: '100vw',
  },
  mapFrame: {
    width: '100%',
    height: '550px',
    border: 0,
    boxSizing: 'border-box',
  },
  '@media (max-width: 768px)': {
    contentWrapper: {
      gridTemplateColumns: '1fr',
      gap: '2rem',
    },
    container: {
      padding: '2rem 1rem',
    },
  },
  messageBox: {
    padding: '12px 16px',
    marginBottom: '20px',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  editIconContainer: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
  editIcon: {
    fontSize: '20px',
  },
  contactContent: {
    color: 'white',
    lineHeight: '1.6',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  loadingSpinner: {
    border: '4px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTop: '4px solid white',
    width: '30px',
    height: '30px',
    animation: 'spinner 1s linear infinite',
    marginBottom: '1rem',
  },
  loadingText: {
    color: 'white',
    margin: 0,
  },
  facebookUrlButton: {
    position: 'absolute',
    top: '60px',
    right: '10px',
    backgroundColor: '#4267B2', // Facebook blue
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#365899', // Darker Facebook blue on hover
    }
  },
};
